import { makeStyles } from "@mui/styles";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AutoSizer, Collection } from "react-virtualized";

import { addCurrentSelectedItems, removeCurrentSelectedItems, setSelectAll } from "../../../store/actions";
import { currentSelectedItemsSelector } from "../../../store/selectors";
import MediaItemCell from "../MediaItemCell";

const useStyles = makeStyles({
  reactVirtualizedCollection: {
    width: "100%",
    height: "100%",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0, 0, 0, 0.2)",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "& .ReactVirtualized__Collection__innerScrollContainer": {},
  },
  collectionItem: {
    padding: "10px",
    boxSizing: "border-box",
    cursor: "pointer",
  },
});

function FullscreenVirtualizedCollection(props) {
  const {
    list,
    minWidth = 120,
    minHeightGrow = 1.2,
    selectModeEnabled,
    width,
    height,
    propertyId,
    experienceId,
    addNewExperience,
    experienceMediaItems,
    params,
    locked,
    startingAssetId,
    handleSetStartingPoint,
  } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const { selectMode = false, selectedItems = {} } = useSelector(currentSelectedItemsSelector);

  const collectionRef = useRef(null);
  const containerWidth = width - 20;
  let columnYMap = [];

  const [prevIndex, setPrevIndex] = useState(null);

  useEffect(() => {
    setPrevIndex(null);
  }, [selectMode]);

  useEffect(() => {
    if (selectModeEnabled) {
      const numberOfSelectedItems = Object.keys(selectedItems).length;
      dispatch(setSelectAll(list.length && list.length === numberOfSelectedItems));
    }
  }, [JSON.stringify(selectedItems)]);

  const handleItemClick = ({ index, rowData, event }) => {
    const { id } = rowData;
    const { shiftKey } = event;

    if (Object.hasOwn(selectedItems, id)) {
      dispatch(removeCurrentSelectedItems([id]));
      setPrevIndex(null);
    } else {
      const newSelectedItems = {};
      newSelectedItems[id] = rowData;

      if ((prevIndex || prevIndex === 0) && shiftKey) {
        const [startIndex, endIndex] = prevIndex > index ? [index, prevIndex] : [prevIndex, index];

        list.slice(startIndex, endIndex).forEach((item) => {
          newSelectedItems[item.id] = item;
        });

        setPrevIndex(null);
        dispatch(addCurrentSelectedItems(newSelectedItems, "add"));
      } else {
        setPrevIndex(index);
        dispatch(addCurrentSelectedItems(newSelectedItems, "add"));
      }
    }
  };

  const cellSizeAndPositionGetter = ({ index }) => {
    // If containerWidth is less than minWidth, we should simply set
    // the columnCount to 1 anyways to prevent AutoSizer from crashing,
    // which fixes Error: Invalid metadata returned for cell 0:
    //     x:NaN, y:Infinity, width:Infinity, height:Infinity]
    const columnCount = Math.floor(containerWidth / minWidth) || 1;

    const columnPosition = index % columnCount;
    const GUTTER_SIZE = 0;
    const STARTING_POINT_BUTTON_SIZE = 50;

    const cellWidth = containerWidth / columnCount;
    const cellHeight = cellWidth * minHeightGrow + STARTING_POINT_BUTTON_SIZE;
    const x = columnPosition * (GUTTER_SIZE + cellWidth);
    const y = columnYMap[columnPosition] || 0;

    columnYMap[columnPosition] = y + cellHeight + GUTTER_SIZE;

    return { x, y, width: cellWidth, height: cellHeight };
  };

  const cellRenderer = ({ index, style }) => {
    const mediaItem = list[index];

    return (
      <div
        key={mediaItem.id}
        className={classes.collectionItem}
        style={style}
        data-testid={`MediaItemCell-${mediaItem.id}`}
      >
        <MediaItemCell
          propertyId={propertyId}
          experienceId={experienceId}
          mediaItem={mediaItem}
          addNewExperience={addNewExperience}
          selectedItems={selectedItems}
          experienceMediaItems={experienceMediaItems}
          params={params}
          onElementSelect={(event) => handleItemClick({ index, rowData: mediaItem, event })}
          disabled={!!experienceId && locked}
          startingAssetId={startingAssetId}
          handleSetStartingPoint={handleSetStartingPoint}
        />
      </div>
    );
  };

  useEffect(() => {
    columnYMap = [];
  }, [list.length]);

  useEffect(() => {
    if (containerWidth > 0 && collectionRef?.current) {
      columnYMap = [];
      collectionRef.current.recomputeCellSizesAndPositions();
    }
  }, [containerWidth, collectionRef]);

  return (
    <Collection
      ref={collectionRef}
      overscanRowCount={10}
      className={classes.reactVirtualizedCollection}
      cellCount={list.length}
      cellRenderer={cellRenderer}
      cellSizeAndPositionGetter={cellSizeAndPositionGetter}
      width={width}
      height={height}
    />
  );
}

export default function FullscreenVirtualizedGrid(props) {
  return (
    <AutoSizer>
      {({ height, width }) =>
        width > 0 ? <FullscreenVirtualizedCollection {...props} height={height} width={width} /> : ""
      }
    </AutoSizer>
  );
}
